import React, { useMemo } from 'react'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  DatePicker as MuiDatePicker,
  DatePickerProps as MuiDatePickerProps,
} from '@mui/x-date-pickers/DatePicker'
import dayjs, { Dayjs } from 'dayjs'

import {
  Chip,
  InputLabel,
  FormLabel as MuiFormLabel,
  FormLabelProps as MuiFormLabelProps,
  styled,
  Typography,
} from '@mui/material'

import { getFilterDateRanges } from '../../features/products/utils/getFilterDateRanges'
import { TestIds } from '../../testIds'
import { DateRangeType } from './types'
import { isValidDate } from '../../util/helpers'
import { colors } from '../../themes/colors'

export interface DateRangePickerProps {
  dateRange: DateRangeType
  setDateRange: (dateRange: DateRangeType) => void
  label?: string
  testId?: string
}

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  [theme.breakpoints.down('lg')]: {
    width: '100%',
  },
}))

const ToFromContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const StyledDatePicker = styled(MuiDatePicker<Dayjs>)<{ error?: boolean }>(({ theme, error }) => ({
  width: '100%',
  [theme.breakpoints.up('lg')]: {
    minWidth: '140px',
  },
  '& .MuiFormControl-root': {
    width: '100%',
  },
  ':focused': {
    ...(error && {
      '& .MuiOutlinedInput-notchedOutline': {
        border: `2px solid ${theme.colors.error700}`,
      },
    }),
  },
  ...(error && {
    '& .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${theme.colors.error700}`,
    },
  }),
}))

const inputStyle: React.CSSProperties = {
  padding: 0,
}

const FormLabel = styled(MuiFormLabel)<MuiFormLabelProps>(() => ({
  display: 'flex',
  width: 'max-content',
  marginBottom: '4px',
  paddingRight: '0',
}))

const Hyphen = styled(Typography)(({ theme }) => ({
  color: theme.colors.grey700,
}))

const ChipsContainer = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  width: '100%',
  justifyContent: 'flex-start',
  gap: 11,
}))

export interface DatePickerProps<T extends Date | Dayjs> {
  setDate?: (date: T | undefined) => void
  label?: string
  testId?: string
  required?: boolean
  error?: boolean
}

export const isWeekend = (date: Dayjs) => {
  return date.day() === 0 || date.day() === 6
}

export const DatePicker = ({
  value,
  setDate,
  label,
  testId,
  required,
  error,
  ...props
}: DatePickerProps<Dayjs> & MuiDatePickerProps<Dayjs>) => {
  const renderLabel = useMemo(() => {
    return (
      <FormLabel>
        {label}
        <InputLabel sx={{ fontWeight: '400', color: colors.grey700, mb: 0, ml: 1 }}>
          {required ? '(Required)' : ''}
        </InputLabel>
      </FormLabel>
    )
  }, [label, required])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container>
        {label && renderLabel}
        <StyledDatePicker
          error={error}
          aria-describedby='date-error-msg'
          value={value !== undefined ? dayjs(value) : null}
          format='DD/MM/YY'
          onChange={(newValue) => {
            let value = newValue
            if (value === null || isValidDate(value?.toDate())) {
              value = value?.startOf('day') ?? null
              if (setDate) setDate(value ?? undefined)
            }
          }}
          slotProps={{
            popper: {
              sx: {
                zIndex: 9999,
              },
            },
            openPickerButton: {
              color: 'primary',
              id: 'calendar-icon',
            },
            textField: {
              inputProps: {
                style: inputStyle,
                'data-testid': `${testId}`,
              },
            },
          }}
          shouldDisableDate={isWeekend}
          {...props}
        />
      </Container>
    </LocalizationProvider>
  )
}

const DateRangePicker = ({ dateRange, setDateRange, label, testId }: DateRangePickerProps) => {
  const renderLabel = useMemo(() => {
    return <FormLabel>{label}</FormLabel>
  }, [label])

  React.useLayoutEffect(() => {
    // Have to do it this way, the package is ignoring all components and children changes
    try {
      const element = document.getElementById('calendar-from-icon') as HTMLElement
      element.innerHTML = "<span class='material-symbols-outlined'>calendar_today</span>"

      const element2 = document.getElementById('calendar-to-icon') as HTMLElement
      element2.innerHTML = "<span class='material-symbols-outlined'>calendar_today</span>"
    } catch (error) {
      console.log('Supressed: ', error)
    }
  })

  const dateRanges = getFilterDateRanges()

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container>
        {label && renderLabel}
        <ToFromContainer>
          <StyledDatePicker
            aria-describedby='date-error-msg'
            value={dateRange.from !== undefined ? dayjs(dateRange.from) : null}
            format='DD/MM/YY'
            maxDate={dateRange.to ? dayjs(dateRange.to) : undefined}
            onChange={(newValue) => {
              let value = newValue
              if (value === null || isValidDate(value?.toDate())) {
                value = value?.startOf('day') ?? null
                setDateRange({ ...dateRange, from: value?.toDate() })
              }
            }}
            slotProps={{
              popper: {
                sx: {
                  zIndex: 9999,
                },
              },
              openPickerButton: {
                color: 'primary',
                id: 'calendar-from-icon',
              },
              textField: {
                inputProps: {
                  style: { ...inputStyle },
                  placeholder: 'From',
                  'data-testid': `${testId}-from`,
                },
              },
            }}
          />
          <Hyphen variant='caption'>-</Hyphen>
          <StyledDatePicker
            aria-describedby='date-error-msg'
            value={dateRange.to !== undefined ? dayjs(dateRange.to) : null}
            format='DD/MM/YY'
            minDate={dateRange.from ? dayjs(dateRange.from) : undefined}
            onChange={(newValue) => {
              let value = newValue
              if (value === null || isValidDate(value?.toDate())) {
                value = value?.endOf('day') ?? null
                setDateRange({ ...dateRange, to: value?.toDate() })
              }
            }}
            slotProps={{
              popper: {
                sx: {
                  zIndex: 9999,
                },
              },
              openPickerButton: {
                color: 'primary',
                id: 'calendar-to-icon',
              },
              textField: {
                inputProps: {
                  style: inputStyle,
                  placeholder: 'To',
                  'data-testid': `${testId}-to`,
                },
              },
            }}
          />
        </ToFromContainer>
        {dayjs(dateRange.to).isBefore(dayjs(dateRange.from)) && (
          <Typography
            variant='body2'
            id='date-error-msg'
            color='error'
            data-testid={`${testId}-error`}
            sx={{ mt: 0.5 }}
          >
            From date must be before To date
          </Typography>
        )}
      </Container>

      <ChipsContainer>
        {dateRanges.map((chip) => (
          <Chip
            color='primary'
            data-testid={TestIds.components.filters.timeSelector}
            data-testdata={chip.label}
            key={chip.label}
            label={chip.label}
            onClick={() => setDateRange({ ...chip })}
          />
        ))}
      </ChipsContainer>
    </LocalizationProvider>
  )
}

export default DateRangePicker
